import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Filter } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API } from '../App';
import { normalizeProductImages, toAbsoluteImageUrl, normalizeCategoryImage } from '../utils/image';

const Shop = () => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  useEffect(() => {
    if (category) {
      setSelectedCategory(category);
    }
  }, [category]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.map(normalizeCategoryImage));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/products`
        : `${API}/products?category=${selectedCategory}`;
      const response = await axios.get(url);
      setProducts(response.data.map(normalizeProductImages));
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="shop-page">
      <Navbar />
      
      <div className="shop-container">
        <div className="shop-header" data-testid="shop-header">
          <h1 className="shop-title">Shop All Products</h1>
          <p className="shop-subtitle">Discover our unique handcrafted creations</p>
        </div>

        {/* Category Filter */}
        <div className="filter-section" data-testid="filter-section">
          <div className="filter-header">
            <Filter size={20} />
            <span>Filter by Category</span>
          </div>
          <div className="category-filters">
            <button
              className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
              data-testid="filter-all"
            >
              All Products
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`filter-btn ${selectedCategory === cat.slug ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.slug)}
                data-testid={`filter-${cat.slug}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="loading" data-testid="loading-indicator">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="no-products" data-testid="no-products">
            <p>No products found in this category.</p>
          </div>
        ) : (
          <div className="products-grid" data-testid="products-grid">
            {products.map((product) => {
              const image = toAbsoluteImageUrl(product.images[0]) || 'https://via.placeholder.com/400x300?text=No+Image';

              return (
              <Link
                key={product.id}
                to={`/product/${product.slug}`}
                className="product-card"
                data-testid={`product-card-${product.slug}`}
              >
                <div className="product-image-wrapper">
                  <img
                    src={image}
                    alt={product.name}
                    className="product-image"
                    data-testid={`product-image-${product.slug}`}
                  />
                  {product.inventory <= 5 && product.inventory > 0 && (
                    <div className="low-stock-badge">Only {product.inventory} left</div>
                  )}
                  {product.inventory === 0 && (
                    <div className="out-of-stock-badge">Out of Stock</div>
                  )}
                </div>
                <div className="product-info">
                  <h3 className="product-name" data-testid={`product-name-${product.slug}`}>
                    {product.name}
                  </h3>
                  <p className="product-price" data-testid={`product-price-${product.slug}`}>
                    ${product.price.toFixed(2)}
                  </p>
                </div>
              </Link>
            );
            })}
          </div>
        )}
      </div>

      <Footer />

      <style jsx>{`
        .shop-container {
          margin-top: 80px;
          padding: 3rem 2rem;
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
          min-height: calc(100vh - 200px);
        }

        .shop-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .shop-title {
          font-size: 3rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 0.5rem;
        }

        .shop-subtitle {
          font-size: 1.1rem;
          color: #718096;
        }

        .filter-section {
          margin-bottom: 3rem;
          padding: 2rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .filter-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 1rem;
        }

        .category-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .filter-btn {
          padding: 0.75rem 1.5rem;
          border: 2px solid #E2E8F0;
          background: white;
          border-radius: 50px;
          font-weight: 500;
          color: #4A5568;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
          white-space: nowrap;
        }

        .filter-btn:hover {
          border-color: #FF9B9B;
          color: #FF9B9B;
          transform: translateY(-2px);
        }

        .filter-btn.active {
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
          border-color: transparent;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 2rem;
        }

        .product-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          text-decoration: none;
          color: #2D3748;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          position: relative;
        }

        .product-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(255, 155, 155, 0.2);
        }

        .product-image-wrapper {
          width: 100%;
          height: 320px;
          overflow: hidden;
          background: #F7FAFC;
          position: relative;
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .product-card:hover .product-image {
          transform: scale(1.05);
        }

        .low-stock-badge, .out-of-stock-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .low-stock-badge {
          background: #FFA500;
          color: white;
        }

        .out-of-stock-badge {
          background: #E53E3E;
          color: white;
        }

        .product-info {
          padding: 1.5rem;
        }

        .product-name {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }

        .product-price {
          font-size: 1.3rem;
          font-weight: 700;
          color: #FF9B9B;
        }

        .loading, .no-products {
          text-align: center;
          padding: 4rem 2rem;
          font-size: 1.1rem;
          color: #718096;
        }

        @media (max-width: 768px) {
          .shop-container {
            padding: 2rem 1rem;
            margin-top: 60px;
          }

          .shop-title {
            font-size: 2rem;
          }

          .filter-section {
            padding: 1.5rem 1rem;
          }

          .filter-header {
            font-size: 0.9rem;
          }

          .category-filters {
            gap: 0.5rem;
          }

          .filter-btn {
            padding: 0.6rem 1rem;
            font-size: 0.85rem;
            flex: 1 1 auto;
            min-width: calc(50% - 0.25rem);
            text-align: center;
          }

          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
            gap: 1rem;
          }

          .product-card {
            border-radius: 15px;
          }

          .product-image-wrapper {
            height: 180px;
          }

          .product-info {
            padding: 1rem;
          }

          .product-name {
            font-size: 0.95rem;
          }

          .product-price {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Shop;